import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { partnerLogos } from '../data/partnerLogos';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import Header from './Header';
import Footer from './Footer';

// Format nama dari filename: "check-point.png" → "Check Point"
const formatPartnerName = (filename) => {
  const name = filename.replace(/\.(png|webp|jpg|jpeg)$/i, '').replace(/[-_]/g, ' ');
  return name.replace(/\b\w/g, (c) => c.toUpperCase());
};

const PartnersDetail = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [failedLogos, setFailedLogos] = useState(() => new Set());

  const markLogoFailed = (filename) => {
    setFailedLogos((prev) => {
      if (prev.has(filename)) return prev;
      return new Set([...prev, filename]);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      <Header lightBackground />
      <div className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-12 border-orange-600 text-orange-600 hover:bg-orange-50"
          >
            <ArrowLeft className="mr-2" size={18} />
            {t(language, 'serviceDetail.back')}
          </Button>

          {/* Page Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              {language === 'id' ? 'Semua' : 'All'}{' '}
              <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                {t(language, 'partners.subtitle')}
              </span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t(language, 'partners.description')}
            </p>
          </div>

          {/* Partners Grid - All logos in one page */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {partnerLogos.map((filename, index) => {
              const displayName = formatPartnerName(filename);
              const src = `/images/partners/${filename}`;
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-8 flex flex-col items-center justify-center min-h-[180px] hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-orange-200"
                >
                  <div className="w-full h-24 flex items-center justify-center relative overflow-hidden">
                    {failedLogos.has(filename) ? (
                      <span className="text-gray-700 font-semibold text-sm text-center px-2">
                        {displayName}
                      </span>
                    ) : (
                      <img
                        src={src}
                        alt={displayName}
                        loading="lazy"
                        decoding="async"
                        className="max-w-full max-h-full object-contain transition-all duration-300"
                        onError={() => markLogoFailed(filename)}
                        title={displayName}
                      />
                    )}
                  </div>
                  <p className="mt-4 text-sm font-medium text-gray-600 text-center group-hover:text-orange-600 transition-colors">
                    {displayName}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center">
            <Button
              onClick={() => navigate('/', { state: { scrollTo: 'contact' } })}
              className="text-white hover:brightness-110 px-8 py-6 text-lg font-semibold"
              style={{ background: 'linear-gradient(56deg, rgba(13, 41, 67, 1) 0%, rgba(25, 54, 112, 1) 100%)' }}
            >
              {t(language, 'serviceDetail.freeConsultation')}
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PartnersDetail;


